/**
 * AirCap Logic Types
 * 
 * TypeScript interfaces for Airport Capacity Intelligence module.
 */

// ==================== Enums ====================

export type SubsystemType =
    | 'CHECK_IN'
    | 'SELF_SERVICE_KIOSK'
    | 'CHECK_IN_QUEUE'
    | 'DEPARTURE_LOBBY'
    | 'SECURITY_SCREENING'
    | 'PASSPORT_CONTROL_DEP'
    | 'DEPARTURE_LOUNGE'
    | 'BOARDING_GATE'
    | 'PASSPORT_CONTROL_ARR'
    | 'BAGGAGE_CLAIM'
    | 'CUSTOMS'
    | 'ARRIVALS_HALL'
    | 'CURBSIDE_DEPARTURES'
    | 'CURBSIDE_ARRIVALS';

export type LevelOfService = 'OPTIMUM' | 'SUB_OPTIMUM' | 'UNDER_PROVIDED';

// ==================== DDFS Types ====================

export interface DDFSFlight {
    flight_id: string;
    scheduled_time: string;
    direction: 'ARR' | 'DEP';
    aircraft_type: string;
    seats: number;
    destination?: string;
    origin?: string;
    airline_code?: string;
    terminal?: string;
    category?: 'NARROW_BODY' | 'WIDE_BODY';
    estimated_pax?: number;
}

export interface DDFSSchedule {
    id: string;
    name: string;
    total_flights: number;
    departures_count: number;
    arrivals_count: number;
    php_departures: number;
    php_arrivals: number;
    php_total: number;
    peak_hour_dep: number | null;
    peak_hour_arr: number | null;
    narrow_body_pct: number;
    created_at: string;
}

// ==================== Parameter Types ====================

export interface ParameterSet {
    load_factor: number;
    transfer_pax_pct: number;
    companion_ratio: number;
    checkin_traditional_pct: number;
    checkin_process_time_min: number;
    checkin_space_per_pax: number;
    selfservice_pct: number;
    selfservice_process_time_sec: number;
    selfservice_max_queue_min: number;
    bagdrop_pct: number;
    bagdrop_process_time_min: number;
    security_process_time_sec: number;
    security_load_factor: number;
    peak_30min_factor: number;
    immigration_dep_process_time_min: number;
    immigration_arr_process_time_min: number;
    lounge_area_seated: number;
    lounge_area_standing: number;
    lounge_circulation_factor: number;
    lounge_seat_ratio: number;
    departure_lobby_dwell_time_min: number;
    departure_lobby_space_per_person: number;
    baggage_frontage_per_pax: number;
    baggage_recirculation_rate: number;
    pax_with_baggage_pct: number;
    customs_inspection_pct: number;
    customs_space_per_pax: number;
    curbside_vehicle_length: number;
    curbside_dwell_time_min: number;
    curbside_vehicle_occupancy: number;
    curbside_private_car_pct: number;
    // Infrastructure Inventory (added for UI expansion)
    terminal_id?: string;
    checkin_counters?: number;
    security_lanes?: number;
    passport_positions_dep?: number;
    passport_positions_arr?: number;
    gates_domestic?: number;
    gates_international?: number;
    gates_schengen?: number;
    gates_remote?: number;
    gates_jetbridge?: number;
}

export interface ParameterConfigRequest {
    [key: string]: number | undefined;
}

// ==================== Inventory Types ====================

export interface SubsystemInventory {
    subsystem: SubsystemType;
    available_capacity: number;
    unit: string;
}

export interface InventoryConfig {
    airport_icao: string;
    items: SubsystemInventory[];
}

// ==================== Result Types ====================

export interface SubsystemResult {
    id: string;
    subsystem: SubsystemType;
    required_capacity: number;
    available_capacity: number;
    unit: string;
    level_of_service: LevelOfService;
    utilization_pct: number;
    formula_name: string;
    formula_latex: string;
    parameters_used: Record<string, number>;
    source_reference: string;
}

export interface CapacityAnalysis {
    id: string;
    schedule_id: string;
    airport_icao: string;
    analysis_mode: 'STATIC' | 'DES';
    parameters: ParameterSet;
    subsystem_results: SubsystemResult[];
    generated_at: string;
    php_departures: number;
    php_arrivals: number;
    overall_los: LevelOfService;
    bottlenecks: SubsystemResult[];
    critical_count: number;
    optimum_count: number;
    total_subsystems: number;
}

export interface AnalysisSummary {
    id: string;
    airport_icao: string;
    php_total: number;
    overall_los: LevelOfService;
    critical_count: number;
    optimum_count: number;
    total_subsystems: number;
    bottleneck_subsystems: string[];
}

// ==================== Request Types ====================

export interface CalculationRequest {
    schedule_id: string;
    airport_icao: string;
    parameters?: ParameterConfigRequest;
    inventory?: InventoryConfig;
}

// ==================== Formula Reference ====================

export interface FormulaReference {
    name: string;
    latex: string;
    source: string;
    variables: Record<string, string>;
}

// ==================== UI Helper Types ====================

export interface SubsystemInfo {
    id: SubsystemType;
    name: string;
    category: 'DEPARTURE' | 'ARRIVAL' | 'COMMON';
    unit: string;
}

export const SUBSYSTEM_INFO: Record<SubsystemType, SubsystemInfo> = {
    CHECK_IN: { id: 'CHECK_IN', name: 'Check-in Counters', category: 'DEPARTURE', unit: 'counters' },
    SELF_SERVICE_KIOSK: { id: 'SELF_SERVICE_KIOSK', name: 'Self-Service Kiosks', category: 'DEPARTURE', unit: 'kiosks' },
    CHECK_IN_QUEUE: { id: 'CHECK_IN_QUEUE', name: 'Check-in Queue Area', category: 'DEPARTURE', unit: 'm²' },
    DEPARTURE_LOBBY: { id: 'DEPARTURE_LOBBY', name: 'Departure Lobby', category: 'DEPARTURE', unit: 'm²' },
    SECURITY_SCREENING: { id: 'SECURITY_SCREENING', name: 'Security Screening', category: 'DEPARTURE', unit: 'lanes' },
    PASSPORT_CONTROL_DEP: { id: 'PASSPORT_CONTROL_DEP', name: 'Passport Control (Dep)', category: 'DEPARTURE', unit: 'positions' },
    DEPARTURE_LOUNGE: { id: 'DEPARTURE_LOUNGE', name: 'Departure Lounge', category: 'DEPARTURE', unit: 'm²' },
    BOARDING_GATE: { id: 'BOARDING_GATE', name: 'Boarding Gates', category: 'DEPARTURE', unit: 'gates' },
    PASSPORT_CONTROL_ARR: { id: 'PASSPORT_CONTROL_ARR', name: 'Passport Control (Arr)', category: 'ARRIVAL', unit: 'positions' },
    BAGGAGE_CLAIM: { id: 'BAGGAGE_CLAIM', name: 'Baggage Claim', category: 'ARRIVAL', unit: 'belts' },
    CUSTOMS: { id: 'CUSTOMS', name: 'Customs Area', category: 'ARRIVAL', unit: 'm²' },
    ARRIVALS_HALL: { id: 'ARRIVALS_HALL', name: 'Arrivals Hall', category: 'ARRIVAL', unit: 'm²' },
    CURBSIDE_DEPARTURES: { id: 'CURBSIDE_DEPARTURES', name: 'Curbside (Dep)', category: 'DEPARTURE', unit: 'm' },
    CURBSIDE_ARRIVALS: { id: 'CURBSIDE_ARRIVALS', name: 'Curbside (Arr)', category: 'ARRIVAL', unit: 'm' },
};

// Level of Service colors
export const LOS_COLORS: Record<LevelOfService, string> = {
    OPTIMUM: '#22c55e',      // Green
    SUB_OPTIMUM: '#f59e0b',  // Yellow/Amber
    UNDER_PROVIDED: '#ef4444', // Red
};

export const LOS_LABELS: Record<LevelOfService, string> = {
    OPTIMUM: 'Optimum',
    SUB_OPTIMUM: 'Sub-Optimum',
    UNDER_PROVIDED: 'Under-Provided',
};
